// frontend/src/components/GridPlanner.js
//
// The planner behind /tools/instagram-grid-planner.
//
// THE THING MOST GRID PLANNERS NOW GET WRONG. Instagram changed the profile grid
// from square to 4:5 PORTRAIT on mobile in early 2025. Desktop still shows 1:1
// squares. So a profile genuinely looks different depending on where it is
// viewed, and an image composed to survive one crop can be ruined by the other.
// Verified 2026-08-20 (Buffer's grid guide, Fstoppers, SocialBee's 2026 size
// guide all agree, and all note the desktop/mobile split).
//
// Every planner that still renders a square-only grid is previewing a layout
// most of your audience does not see. This one renders BOTH and shows exactly
// what each crop removes — which is the whole reason to use it rather than
// arranging thumbnails in Canva.
//
// Nothing is uploaded. Images become object URLs and stay on the device, which
// is both a genuine privacy advantage over the app-based competitors and the
// reason this costs nothing to run.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, ButtonGroup, Form, Badge } from 'react-bootstrap'
import { Trash, Upload, ArrowClockwise, EyeFill } from 'react-bootstrap-icons'

let seq = 0

/** How much of the image height (or width) a crop discards, as a percentage. */
function cropLoss (w, h, targetRatio) {
  if (!w || !h) return 0
  const imgRatio = w / h
  if (imgRatio > targetRatio) {
    // Too wide — the sides get cut.
    return Math.round((1 - targetRatio / imgRatio) * 100)
  }
  // Too tall — the top and bottom get cut.
  return Math.round((1 - imgRatio / targetRatio) * 100)
}

export default function GridPlanner () {
  const [items, setItems] = useState([])
  const [view, setView] = useState('mobile')     // 'mobile' 4:5 | 'desktop' 1:1
  const dragFrom = useRef(null)
  const inputRef = useRef(null)

  // Object URLs leak if they are never revoked, but revoking them on every
  // `items` change would kill the URLs of images still on screen — the effect
  // cleanup runs before each re-run, so adding a second batch would blank the
  // first. Hold the latest list in a ref and revoke ONLY on unmount.
  const itemsRef = useRef(items)
  itemsRef.current = items
  useEffect(() => () => { itemsRef.current.forEach(i => URL.revokeObjectURL(i.url)) }, [])

  const addFiles = useCallback(files => {
    const picked = Array.from(files || []).filter(f => f.type.startsWith('image/'))
    if (!picked.length) return
    const next = picked.map(f => ({
      id: ++seq,
      url: URL.createObjectURL(f),
      name: f.name,
      w: 0,
      h: 0,
    }))
    // New posts land at the FRONT of an Instagram grid and push everything down,
    // which is the single most common surprise when planning one. Match it.
    setItems(prev => [...next, ...prev])
  }, [])

  const onLoad = (id, e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target
    setItems(prev => prev.map(i => (i.id === id ? { ...i, w, h } : i)))
  }

  const remove = id => setItems(prev => {
    const gone = prev.find(i => i.id === id)
    if (gone) URL.revokeObjectURL(gone.url)
    return prev.filter(i => i.id !== id)
  })

  const clear = () => {
    items.forEach(i => URL.revokeObjectURL(i.url))
    setItems([])
  }

  const onDrop = to => {
    const from = dragFrom.current
    dragFrom.current = null
    if (from === null || from === to) return
    setItems(prev => {
      const next = prev.slice()
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }

  const ratio = view === 'mobile' ? 4 / 5 : 1
  const ratioCss = view === 'mobile' ? '4 / 5' : '1 / 1'

  return (
    <div className='p-4 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
      <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
        <Button variant='primary' onClick={() => inputRef.current?.click()}>
          <Upload className='me-1' />Add images
        </Button>
        <Form.Control ref={inputRef} type='file' accept='image/*' multiple className='d-none'
                      onChange={e => { addFiles(e.target.files); e.target.value = '' }} />

        <ButtonGroup size='sm'>
          <Button variant={view === 'mobile' ? 'secondary' : 'outline-secondary'}
                  onClick={() => setView('mobile')}>Mobile — 4:5</Button>
          <Button variant={view === 'desktop' ? 'secondary' : 'outline-secondary'}
                  onClick={() => setView('desktop')}>Desktop — 1:1</Button>
        </ButtonGroup>

        {items.length > 0 && (
          <Button size='sm' variant='outline-danger' onClick={clear}>
            <ArrowClockwise className='me-1' />Clear
          </Button>
        )}
        <span className='text-muted small ms-auto'>
          Nothing is uploaded — the images stay on your device.
        </span>
      </div>

      {items.length === 0 ? (
        <div
          className='text-center p-5 rounded-3'
          style={{ border: '2px dashed #d0d5dd', background: '#f8f9fa' }}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files) }}
        >
          <EyeFill size={28} className='text-muted mb-2' />
          <p className='mb-1 fw-semibold'>Drop images here, or use Add images</p>
          <p className='text-muted small mb-0'>
            They are arranged newest-first, exactly as Instagram does it.
          </p>
        </div>
      ) : (
        <>
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 3, maxWidth: 468 }}
            onDragOver={e => e.preventDefault()}
          >
            {items.map((it, idx) => {
              const loss = cropLoss(it.w, it.h, ratio)
              return (
                <div
                  key={it.id}
                  draggable
                  onDragStart={() => { dragFrom.current = idx }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => onDrop(idx)}
                  className='position-relative'
                  style={{ aspectRatio: ratioCss, overflow: 'hidden', background: '#eee', cursor: 'grab' }}
                  title={`${it.name}${loss ? ` — ${loss}% cropped in this view` : ''}`}
                >
                  <img
                    src={it.url}
                    alt={it.name}
                    onLoad={e => onLoad(it.id, e)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  {loss > 20 && (
                    <span className='position-absolute top-0 start-0 m-1 badge bg-danger'
                          style={{ fontSize: '0.6rem' }}>
                      {loss}% cut
                    </span>
                  )}
                  <button type='button'
                          className='btn btn-sm btn-light position-absolute bottom-0 end-0 m-1 p-1 lh-1'
                          style={{ opacity: 0.85 }}
                          aria-label={`Remove ${it.name}`}
                          onClick={() => remove(it.id)}>
                    <Trash size={12} />
                  </button>
                </div>
              )
            })}
          </div>

          <div className='d-flex flex-wrap gap-3 align-items-center mt-3'>
            <Badge bg='secondary'>{items.length} posts</Badge>
            <span className='text-muted small'>
              Drag any tile to reorder. New posts appear top-left and push the rest down.
            </span>
          </div>

          {/* The crop comparison — the reason this exists */}
          {items.some(i => cropLoss(i.w, i.h, 4 / 5) !== cropLoss(i.w, i.h, 1)) && (
            <div className='mt-3 p-3 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#f8f9fa' }}>
              <div className='fw-semibold mb-1'>These crop differently on mobile and desktop</div>
              <p className='text-muted small mb-0'>
                Instagram shows a 4:5 portrait grid in the app and a 1:1 square grid on the web.
                Switch the toggle above to see both. Anything important near the top or bottom
                edge survives one and not the other — which is why a feed that looks composed on
                a phone can fall apart on desktop.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
